import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../context/AuthContext';
import { useLocations as useLocationsContext } from '../../context/LocationsContext';
import { useBusinesses } from '../../context/BusinessesContext';
import Image from '../AppImage';
import { fetchCurrentEmployee } from '../../lib/api/employees';
import useTranslation from '../../hooks/useTranslation';
import useClickOutside from '../../hooks/useClickOutside';
import useNotifications from '../../hooks/useNotifications';

const normalizeValue = (value) => {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (value === null || value === undefined) {
    return '';
  }
  const asString = value.toString?.();
  return typeof asString === 'string' ? asString.trim() : '';
};

const Header = ({
  isCollapsed = false,
  onToggleSidebar = () => {},
  userRole: userRoleProp = 'manager',
  selectedLocation = '',
  locations: locationsProp,
  onLocationChange = () => {},
  allowLocationSelection = true,
  notifications = {
    messages: 3,
    timeOff: 2
  }
}) => {
  const { t } = useTranslation(['header', 'common']);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, logout } = useAuth();
  const { locations: contextLocations, loading: locationsLoading } = useLocationsContext();
  const { businesses: contextBusinesses, loading: businessesLoading } = useBusinesses();
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
  const [employeeLocationName, setEmployeeLocationName] = useState(null);
  const [loadingEmployeeLocation, setLoadingEmployeeLocation] = useState(false);
  const [hasLogoError, setHasLogoError] = useState(false);

  const defaultLogoSrc = '/assets/images/logo-black-on-white.png';

  const moreMenuRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const userMenuRef = useRef(null);
  const notificationMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileToggleRef = useRef(null);

  const closeMoreMenu = useCallback(() => setIsMoreMenuOpen(false), []);
  const closeLocationDropdown = useCallback(() => setIsLocationDropdownOpen(false), []);
  const closeUserMenu = useCallback(() => setIsUserMenuOpen(false), []);
  const closeNotificationMenu = useCallback(
    () => setIsNotificationMenuOpen(false),
    [],
  );
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const mobileMenuRefs = useMemo(() => [mobileMenuRef, mobileToggleRef], [mobileMenuRef, mobileToggleRef]);

  useClickOutside(moreMenuRef, closeMoreMenu, isMoreMenuOpen);
  useClickOutside(locationDropdownRef, closeLocationDropdown, isLocationDropdownOpen);
  useClickOutside(userMenuRef, closeUserMenu, isUserMenuOpen);
  useClickOutside(notificationMenuRef, closeNotificationMenu, isNotificationMenuOpen);
  useClickOutside(mobileMenuRefs, closeMobileMenu, isMobileMenuOpen);

  const {
    notifications: userNotifications,
    unseenCount,
    markAsSeen,
    markAllAsSeen,
  } = useNotifications(10000);

  const avatarSrc =
    (typeof user?.avatar === 'string' ? user.avatar : null)
    ?? (typeof user?.avatarUrl === 'string' ? user.avatarUrl : null)
    ?? (typeof user?.profile?.avatarUrl === 'string' ? user.profile.avatarUrl : null)
    ?? (typeof user?.employee?.avatarUrl === 'string' ? user.employee.avatarUrl : null)
    ?? (typeof user?.raw?.avatarUrl === 'string' ? user.raw.avatarUrl : null)
    ?? (typeof user?.profile?.avatar === 'string' ? user.profile.avatar : null)
    ?? (typeof user?.employee?.avatar === 'string' ? user.employee.avatar : null)
    ?? null;

  const effectiveRole = user?.role ?? userRoleProp;
  const displayName = user?.fullName ?? t('header.labels.teamMember');

  const locationStorageKey = useMemo(() => {
    const identifier =
      user?.id
      ?? user?._id
      ?? user?.email?.toLowerCase?.()
      ?? 'anonymous';
    return `restaurant-manager::location-selection::${identifier}`;
  }, [user?._id, user?.email, user?.id]);

  const [sessionSelection, setSessionSelection] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !locationStorageKey) {
      return;
    }

    try {
      const rawValue = window.sessionStorage.getItem(locationStorageKey);
      if (!rawValue) {
        setSessionSelection(null);
        return;
      }

      let parsedValue = null;
      try {
        parsedValue = JSON.parse(rawValue);
      } catch {
        parsedValue = rawValue;
      }

      if (parsedValue && typeof parsedValue === 'object') {
        const normalizedId =
          typeof parsedValue.id === 'string'
            ? parsedValue.id
            : parsedValue.id?.toString?.() ?? null;
        const normalizedName =
          typeof parsedValue.name === 'string' ? parsedValue.name : null;

        if (normalizedId || normalizedName) {
          setSessionSelection({
            id: normalizedId ?? normalizedName ?? null,
            name: normalizedName,
          });
        } else {
          setSessionSelection(null);
        }
        return;
      }

      if (typeof parsedValue === 'string') {
        const trimmed = parsedValue.trim();
        setSessionSelection(trimmed ? { id: trimmed, name: null } : null);
        return;
      }

      setSessionSelection(null);
    } catch {
      setSessionSelection(null);
    }
  }, [locationStorageKey]);

  const normalizedLocations = useMemo(() => {
    if (Array.isArray(locationsProp) && locationsProp.length) {
      return locationsProp;
    }
    if (Array.isArray(contextLocations) && contextLocations.length) {
      return contextLocations;
    }
    return [];
  }, [contextLocations, locationsProp]);

  const normalizedBusinesses = useMemo(() => {
    if (Array.isArray(contextBusinesses) && contextBusinesses.length) {
      return contextBusinesses;
    }
    return [];
  }, [contextBusinesses]);

  const businessLocationMap = useMemo(() => {
    const map = new Map();
    normalizedBusinesses.forEach((business) => {
      const locationIds = Array.isArray(business?.locationIds)
        ? business.locationIds
        : [];
      locationIds.forEach((locationId) => {
        if (typeof locationId !== 'string') {
          return;
        }
        const trimmed = locationId.trim();
        if (!trimmed || map.has(trimmed)) {
          return;
        }
        map.set(trimmed, business);
      });
    });
    return map;
  }, [normalizedBusinesses]);

  const allBranchesOption = useMemo(
    () => ({
      id: '__all__',
      name: t('header.location.allBranches'),
      address: t('header.location.allBranchesDescription'),
      __isAllOption: true,
    }),
    [t],
  );

  const availableLocations = useMemo(
    () => [allBranchesOption, ...normalizedLocations],
    [allBranchesOption, normalizedLocations],
  );

  const propSelectedLocation = useMemo(() => {
    if (!selectedLocation) {
      return null;
    }

    if (typeof selectedLocation === 'string') {
      const trimmed = normalizeValue(selectedLocation);
      if (!trimmed) {
        return null;
      }

      const matchedLocation = normalizedLocations?.find((loc) => {
        if (!loc) {
          return false;
        }

        const locationId = normalizeValue(loc?.id);
        if (locationId && locationId === trimmed) {
          return true;
        }

        const locationName = normalizeValue(loc?.name);
        return Boolean(locationName && locationName === trimmed);
      });

      if (matchedLocation) {
        return matchedLocation;
      }

      if (trimmed === allBranchesOption.name) {
        return allBranchesOption;
      }

      return {
        id: trimmed,
        name: trimmed,
      };
    }

    if (typeof selectedLocation === 'object') {
      if (selectedLocation.__isAllOption) {
        return allBranchesOption;
      }
      return selectedLocation;
    }

    return null;
  }, [allBranchesOption, normalizedLocations, selectedLocation]);

  const sessionSelectedLocation = useMemo(() => {
    if (!sessionSelection) {
      return allBranchesOption;
    }

    const normalizedId = normalizeValue(sessionSelection.id);
    const normalizedName = normalizeValue(sessionSelection.name);

    if (normalizedId === allBranchesOption.id || normalizedName === allBranchesOption.name) {
      return allBranchesOption;
    }

    const locationsToSearch = Array.isArray(normalizedLocations)
      ? normalizedLocations
      : [];

    if (normalizedId) {
      const matchedById = locationsToSearch.find(
        (loc) => normalizeValue(loc?.id) === normalizedId,
      );
      if (matchedById) {
        return matchedById;
      }
    }

    if (normalizedName) {
      const matchedByName = locationsToSearch.find(
        (loc) => normalizeValue(loc?.name) === normalizedName,
      );
      if (matchedByName) {
        return matchedByName;
      }

      return {
        id: normalizedId || normalizedName,
        name: normalizedName,
      };
    }

    if (normalizedId) {
      return {
        id: normalizedId,
        name: sessionSelection.name ?? normalizedId,
      };
    }

    return allBranchesOption;
  }, [allBranchesOption, normalizedLocations, sessionSelection]);

  const resolvedSelectedLocation = useMemo(
    () => propSelectedLocation ?? sessionSelectedLocation,
    [propSelectedLocation, sessionSelectedLocation],
  );

  const [internalSelectedLocation, setInternalSelectedLocation] = useState(() => {
    if (resolvedSelectedLocation?.__isAllOption) {
      return null;
    }
    return resolvedSelectedLocation ?? null;
  });

  const persistSessionSelection = useCallback(
    (locationData) => {
      if (typeof window === 'undefined' || !locationStorageKey) {
        return;
      }

      try {
        if (!locationData || locationData.__isAllOption) {
          window.sessionStorage.removeItem(locationStorageKey);
          setSessionSelection(null);
          return;
        }

        const payload = {
          id: normalizeValue(locationData.id),
          name: normalizeValue(locationData.name) || null,
        };

        window.sessionStorage.setItem(locationStorageKey, JSON.stringify(payload));
        setSessionSelection(payload);
      } catch {
        // Ignore persistence failures to avoid blocking the UI.
      }
    },
    [locationStorageKey, setSessionSelection],
  );

  useEffect(() => {
    const nextInternalSelection = resolvedSelectedLocation?.__isAllOption
      ? null
      : resolvedSelectedLocation ?? null;

    setInternalSelectedLocation((previousSelection) => {
      if (!previousSelection && !nextInternalSelection) {
        return previousSelection;
      }

      const previousId = previousSelection?.id ?? null;
      const nextId = nextInternalSelection?.id ?? null;
      const previousName = previousSelection?.name ?? '';
      const nextName = nextInternalSelection?.name ?? '';

      if (previousId === nextId && previousName === nextName) {
        return previousSelection;
      }

      return nextInternalSelection;
    });
  }, [resolvedSelectedLocation]);

  const effectiveSelectedLocation = useMemo(
    () => internalSelectedLocation ?? resolvedSelectedLocation ?? null,
    [internalSelectedLocation, resolvedSelectedLocation],
  );

  const displayedLocationName = useMemo(() => {
    if (effectiveSelectedLocation?.name) {
      return effectiveSelectedLocation.name;
    }

    if (typeof selectedLocation === 'string') {
      const trimmed = selectedLocation.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (locationsLoading) {
      return t('header.location.loadingLocations');
    }

    return allBranchesOption.name;
  }, [allBranchesOption, effectiveSelectedLocation, locationsLoading, selectedLocation, t]);

  const businessDisplayName = useMemo(() => {
    if (businessesLoading) {
      return t('header.location.loadingBusiness');
    }

    if (!normalizedBusinesses.length) {
      return '—';
    }

    const candidateIds = [];
    const addCandidate = (value) => {
      if (!value) {
        return;
      }
      const trimmed = typeof value === 'string' ? value.trim() : value;
      if (typeof trimmed !== 'string') {
        return;
      }
      if (!trimmed || trimmed === allBranchesOption.id) {
        return;
      }
      candidateIds.push(trimmed);
    };

    addCandidate(effectiveSelectedLocation?.id);
    addCandidate(propSelectedLocation?.id);
    addCandidate(sessionSelectedLocation?.id);

    if (candidateIds.length === 0 && Array.isArray(normalizedLocations) && normalizedLocations.length > 0) {
      normalizedLocations.forEach((loc) => {
        if (loc?.id) {
          addCandidate(loc.id);
        }
      });
    }

    for (const candidate of candidateIds) {
      const business = businessLocationMap.get(candidate);
      if (business?.name) {
        return business.name;
      }
    }

    if (normalizedBusinesses.length === 1) {
      return normalizedBusinesses[0]?.name ?? '—';
    }

    return '—';
  }, [
    allBranchesOption.id,
    businessLocationMap,
    businessesLoading,
    effectiveSelectedLocation?.id,
    normalizedBusinesses,
    normalizedLocations,
    propSelectedLocation?.id,
    sessionSelectedLocation?.id,
    t,
  ]);

  const companyLogoUrl = useMemo(() => {
    const selectLogo = (source) => {
      if (!source || typeof source !== 'object') {
        return null;
      }
      const rawLogo =
        typeof source.logoUrl === 'string' ? source.logoUrl.trim() : '';
      return rawLogo || null;
    };

    const directLogo = selectLogo(company);
    if (directLogo) {
      return directLogo;
    }

    const userSources = [
      user?.company,
      user?.business,
      user?.businessDetails,
      user?.employee?.business,
      user?.employee?.businessDetails,
    ];

    for (const source of userSources) {
      const candidate = selectLogo(source);
      if (candidate) {
        return candidate;
      }
    }

    const businessIdCandidates = [
      typeof company?.id === 'string' ? company.id.trim() : null,
      typeof user?.company?.id === 'string' ? user.company.id.trim() : null,
      typeof user?.business?.id === 'string' ? user.business.id.trim() : null,
      typeof user?.businessId === 'string' ? user.businessId.trim() : null,
      typeof user?.employee?.businessId === 'string'
        ? user.employee.businessId.trim()
        : null,
    ].filter(Boolean);

    for (const candidateId of businessIdCandidates) {
      const matchedBusiness = normalizedBusinesses.find(
        (business) =>
          business &&
          typeof business.id === 'string' &&
          business.id === candidateId,
      );
      const matchedLogo = selectLogo(matchedBusiness);
      if (matchedLogo) {
        return matchedLogo;
      }
    }

    const locationCandidates = [
      effectiveSelectedLocation?.id,
      propSelectedLocation?.id,
      sessionSelectedLocation?.id,
    ];

    for (const locationCandidate of locationCandidates) {
      const normalizedId = normalizeValue(locationCandidate);
      if (!normalizedId) {
        continue;
      }
      const locatedBusiness = businessLocationMap.get(normalizedId);
      const locatedLogo = selectLogo(locatedBusiness);
      if (locatedLogo) {
        return locatedLogo;
      }
    }

    const fallbackBusiness = normalizedBusinesses.find((business) => selectLogo(business));

    return selectLogo(fallbackBusiness);
  }, [
    businessLocationMap,
    company,
    effectiveSelectedLocation,
    normalizedBusinesses,
    propSelectedLocation,
    sessionSelectedLocation,
    user,
  ]);

  useEffect(() => {
    setHasLogoError(false);
  }, [companyLogoUrl]);

  const resolvedLogoSrc =
    !hasLogoError && companyLogoUrl ? companyLogoUrl : defaultLogoSrc;

  const logoAltText =
    businessDisplayName && businessDisplayName !== '—'
      ? businessDisplayName
      : 'Ougama';

  useEffect(() => {
    let isActive = true;

    if (effectiveRole !== 'employee') {
      setEmployeeLocationName(null);
      setLoadingEmployeeLocation(false);
      return () => {
        isActive = false;
      };
    }

    const loadEmployeeLocation = async () => {
      setLoadingEmployeeLocation(true);

      try {
        const employee = await fetchCurrentEmployee();

        if (!isActive) {
          return;
        }

        const derivedLocation = (() => {
          if (!employee) {
            return null;
          }

          if (Array.isArray(employee?.locationNames) && employee.locationNames.length > 0) {
            return employee.locationNames[0];
          }

          if (typeof employee?.location === 'string' && employee.location.trim().length > 0) {
            return employee.location.trim();
          }

          if (
            typeof employee?.locationDetails?.name === 'string' &&
            employee.locationDetails.name.trim().length > 0
          ) {
            return employee.locationDetails.name.trim();
          }

          if (Array.isArray(employee?.locations) && employee.locations.length > 0) {
            const firstLocation = employee.locations.find((assignment) => {
              const name = assignment?.name;
              return typeof name === 'string' && name.trim().length > 0;
            });

            if (firstLocation) {
              return firstLocation.name.trim();
            }
          }

          return null;
        })();

        setEmployeeLocationName(derivedLocation);
      } catch {
        if (!isActive) {
          return;
        }

        setEmployeeLocationName(null);
      } finally {
        if (isActive) {
          setLoadingEmployeeLocation(false);
        }
      }
    };

    loadEmployeeLocation();

    return () => {
      isActive = false;
    };
  }, [effectiveRole, user?.email, user?.id, user?.sub]);

  const employeeLocationDisplayName = useMemo(() => {
    if (effectiveRole !== 'employee') {
      return null;
    }

    if (loadingEmployeeLocation) {
      return t('header.location.loadingEmployeeLocation');
    }

    return employeeLocationName?.trim?.() || '—';
  }, [effectiveRole, employeeLocationName, loadingEmployeeLocation, t]);

  const isLocationActive = (locationOption) => {
    if (!locationOption) {
      return false;
    }

    if (locationOption.__isAllOption) {
      if (effectiveSelectedLocation?.__isAllOption) {
        return true;
      }

      return !effectiveSelectedLocation?.id && !effectiveSelectedLocation?.name;
    }

    if (effectiveSelectedLocation?.__isAllOption) {
      return false;
    }

    if (effectiveSelectedLocation?.id) {
      return effectiveSelectedLocation.id === locationOption.id;
    }

    if (effectiveSelectedLocation?.name) {
      return effectiveSelectedLocation.name === locationOption.name;
    }

    return false;
  };

  const canChangeLocation =
    allowLocationSelection && (effectiveRole === 'manager' || effectiveRole === 'admin');

  const navigationItems = useMemo(
    () => [
      {
        label: t('header.navigation.home'),
        path: '/manager-self-service',
        icon: 'Home',
        roleAccess: ['manager', 'admin'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.schedule'),
        path: '/employee-scheduling',
        icon: 'Calendar',
        roleAccess: ['manager', 'admin'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.staff'),
        path: '/employee-directory',
        icon: 'Users',
        roleAccess: ['manager', 'admin'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.absences'),
        path: '/time-off-management',
        icon: 'Clock',
        roleAccess: ['manager', 'admin', 'employee'],
        notificationKey: effectiveRole === 'employee' ? 'timeOff' : null,
      },
      {
        label: t('header.navigation.chat'),
        path: '/internal-messaging',
        icon: 'MessageSquare',
        roleAccess: ['manager', 'admin', 'employee'],
        notificationKey: 'messages',
      },
      {
        label: t('header.navigation.incidents'),
        path: '/incident-reports',
        icon: 'ShieldAlert',
        roleAccess: ['manager', 'admin', 'employee'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.hiring'),
        path: '/hiring',
        icon: 'Plus',
        roleAccess: ['manager', 'admin'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.trainings'),
        path: '/trainings',
        icon: 'LibraryBig',
        roleAccess: ['manager', 'admin', 'employee'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.home'),
        path: '/employee-self-service',
        icon: 'Home',
        roleAccess: ['employee'],
        notificationKey: null,
      },
      {
        label: t('header.navigation.support'),
        path: '/support',
        icon: 'Shield',
        roleAccess: ['manager', 'admin'],
        notificationKey: null,
      },
    ],
    [effectiveRole, t],
  );

  const filteredNavItems = useMemo(
    () =>
      navigationItems?.filter((item) => item?.roleAccess?.includes(effectiveRole)) ?? [],
    [effectiveRole, navigationItems],
  );

  const orderedFilteredNavItems = useMemo(() => {
    if (effectiveRole !== 'employee') {
      return filteredNavItems;
    }

    const sortedItems = [...filteredNavItems];
    sortedItems.sort((a, b) => {
      const aIsHome = a?.path === '/employee-self-service';
      const bIsHome = b?.path === '/employee-self-service';

      if (aIsHome === bIsHome) {
        return 0;
      }

      return aIsHome ? -1 : 1;
    });

    return sortedItems;
  }, [effectiveRole, filteredNavItems]);

  const preferredPrimaryPathsMap = {
    manager: ['/manager-self-service', '/employee-directory', '/time-off-management'],
    admin: ['/manager-self-service', '/employee-directory', '/time-off-management'],
  };

  const preferredPrimaryPaths = preferredPrimaryPathsMap[effectiveRole] ?? [];

  const preferredPrimaryNavItems =
    preferredPrimaryPaths.length > 0
      ? orderedFilteredNavItems?.filter((item) => preferredPrimaryPaths.includes(item?.path)) ?? []
      : [];

  const primaryNavItems =
    preferredPrimaryNavItems.length > 0
      ? preferredPrimaryNavItems
      : orderedFilteredNavItems?.slice(0, 4) ?? [];

  const secondaryNavItems =
    preferredPrimaryNavItems.length > 0
      ? orderedFilteredNavItems?.filter((item) => !preferredPrimaryPaths.includes(item?.path)) ?? []
      : orderedFilteredNavItems?.slice(primaryNavItems.length) ?? [];

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsMoreMenuOpen(false);
  };

  const handleLocationSelect = (locationData) => {
    if (locationData?.__isAllOption) {
      setInternalSelectedLocation(null);
      onLocationChange(null);
      persistSessionSelection(null);
    } else {
      setInternalSelectedLocation(locationData ?? null);
      onLocationChange(locationData);
      persistSessionSelection(locationData ?? null);
    }
    setIsLocationDropdownOpen(false);
  };

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const NotificationBadge = ({ count, variant = 'corner' }) => {
    const numericCount =
      typeof count === 'string' ? Number.parseInt(count, 10) : count;

    if (!Number.isFinite(numericCount) || numericCount <= 0) {
      return null;
    }

    const displayValue = numericCount > 99 ? '99+' : numericCount;

    if (variant === 'inline') {
      return (
        <span className="ml-2 inline-flex items-center justify-center rounded-full bg-error/10 text-error text-xs font-medium px-2 py-0.5 min-w-[1.75rem]">
          {displayValue}
        </span>
      );
    }

    return (
      <span className="absolute -top-1 -right-1 bg-error text-error-foreground text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center min-w-[1.25rem] z-10">
        {displayValue}
      </span>
    );
  };

  const notificationSectionLabels = useMemo(
    () => ({
      'staff-management': t('header.notifications.sections.staffManagement'),
      'absence-management': t('header.notifications.sections.absenceManagement'),
      'shift-management': t('header.notifications.sections.shiftManagement'),
      'incident-management': t('header.notifications.sections.incidentManagement'),
      'internal-chat': t('header.notifications.sections.internalChat'),
      hiring: t('header.notifications.sections.hiring'),
      'training-management': t('header.notifications.sections.trainingManagement'),
    }),
    [t],
  );

  const notificationSectionTargets = useMemo(
    () => ({
      'staff-management': '/employee-directory',
      'absence-management': '/time-off-management',
      'shift-management': '/employee-scheduling',
      'incident-management': '/incident-reports',
      'internal-chat': '/internal-messaging',
      hiring: '/hiring',
      'training-management': '/trainings',
    }),
    [],
  );

  const formatNotificationTimestamp = (value) => {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) {
      return;
    }

    try {
      await markAsSeen(notification.id);
    } catch (error) {
      // Non-blocking; still allow navigation even if the API call fails
      console.error('Failed to mark notification as seen', error);
    }
    setIsNotificationMenuOpen(false);

    const target =
      notification.targetUrl
      ?? notificationSectionTargets?.[notification.section]
      ?? '/';

    if (typeof target === 'string' && target.trim()) {
      navigate(target);
    }
  };

  const resolveSectionLabel = (section) =>
    notificationSectionLabels?.[section]
    ?? t('header.notifications.sections.generic');

  const showLocationSelector = useMemo(() => {
    const pathname = location?.pathname ?? '';
    if (!pathname) {
      return false;
    }

    const basePaths = ['/employee-directory', '/time-off-management', '/employee-scheduling'];
    return basePaths.some((route) => pathname === route || pathname.startsWith(`${route}/`));
  }, [location?.pathname]);

  return (
    <header className="bg-card border-b border-border h-16 fixed top-0 left-0 right-0 z-50 shadow-elevation-1">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left Section - Logo and Navigation */}
        <div className="flex items-center space-x-6">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={resolvedLogoSrc}
              alt={logoAltText}
              onError={() => setHasLogoError(true)}
              loading="lazy"
              className="h-8 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {primaryNavItems?.map((item) => {
              const isActive = location?.pathname === item?.path;
              return (
                <div key={item?.path} className="relative">
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    onClick={() => handleNavigation(item?.path)}
                    className="relative px-4 py-2 text-sm font-medium transition-hover"
                  >
                    <Icon name={item?.icon} size={16} className="mr-2" />
                    {item?.label}
                  </Button>
                </div>
              );
            })}

            {/* More Menu for Secondary Items */}
            {secondaryNavItems?.length > 0 && (
              <div ref={moreMenuRef} className="relative">
                <Button
                  variant="ghost"
                  onClick={() => setIsMoreMenuOpen((prev) => !prev)}
                  className="px-4 py-2 text-sm font-medium"
                >
                  <Icon name="MoreHorizontal" size={16} className="mr-2" />
                  {t('header.navigation.more')}
                </Button>

                {isMoreMenuOpen && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-elevation-2 py-1 z-50">
                    {secondaryNavItems?.map((item) => {
                      const isActive = location?.pathname === item?.path;
                      return (
                        <button
                          key={item?.path}
                          onClick={() => handleNavigation(item?.path)}
                          className={`w-full flex items-center px-4 py-2 text-sm transition-hover relative ${
                            isActive 
                              ? 'bg-primary text-primary-foreground' 
                              : 'text-popover-foreground hover:bg-muted'
                          }`}
                        >
                    <Icon name={item?.icon} size={16} className="mr-3" />
                    {item?.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        {/* Right Section - Location, User Menu, Mobile Toggle */}
        <div className="flex items-center space-x-4">
          {/* Business Display */}
          <div className="hidden md:flex flex-col items-end">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              {t('header.labels.business')}
            </span>
            <span className="text-sm font-semibold text-foreground">
              {businessDisplayName}
            </span>
          </div>

          {/* Employee Location Display */}
          {effectiveRole === 'employee' && (
            <div className="hidden md:flex flex-col items-end">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                {t('header.labels.location')}
              </span>
              <span className="text-sm font-semibold text-foreground">
                {employeeLocationDisplayName}
              </span>
            </div>
          )}

          {/* Location Selector */}
          {(effectiveRole === 'manager' || effectiveRole === 'admin') && showLocationSelector && (
            <div ref={locationDropdownRef} className="relative location-dropdown hidden md:block">
              {canChangeLocation ? (
                <>
                  <Button
                    variant="outline"
                    onClick={() => setIsLocationDropdownOpen(!isLocationDropdownOpen)}
                    className="text-sm font-medium"
                  >
                    <Icon name="MapPin" size={16} className="mr-2" />
                    {displayedLocationName}
                    <Icon name="ChevronDown" size={16} className="ml-2" />
                  </Button>

                  {isLocationDropdownOpen && (
                    <div className="absolute top-full right-0 mt-1 w-64 bg-popover border border-border rounded-lg shadow-elevation-2 py-1 z-50">
                      {availableLocations?.map((loc) => (
                        <button
                          key={loc?.id ?? `all-${loc?.name}`}
                          onClick={() => handleLocationSelect(loc)}
                          className={`w-full text-left px-4 py-3 transition-hover ${
                            isLocationActive(loc)
                              ? 'bg-primary text-primary-foreground'
                              : 'text-popover-foreground hover:bg-muted'
                          }`}
                        >
                          <div className="font-medium">{loc?.name}</div>
                          {loc?.address && (
                            <div className="text-xs opacity-75">{loc.address}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="px-3 py-2 border border-border rounded-md text-sm text-muted-foreground flex items-center">
                  <Icon name="MapPin" size={16} className="mr-2" />
                  {displayedLocationName}
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          <div ref={notificationMenuRef} className="relative">
            <Button
              variant="ghost"
              onClick={() => setIsNotificationMenuOpen((prev) => !prev)}
              className="relative p-2"
              aria-label={
                isNotificationMenuOpen
                  ? t('header.notifications.closeMenu')
                  : t('header.notifications.openMenu')
              }
              aria-expanded={isNotificationMenuOpen}
            >
              <Icon name="Bell" size={18} />
              <NotificationBadge count={unseenCount} />
            </Button>

            {isNotificationMenuOpen && (
              <div
                className="fixed left-4 right-4 top-16 md:absolute md:top-auto md:right-0 md:left-auto md:mt-2 w-auto md:w-80 max-w-xl bg-popover border border-border rounded-lg shadow-elevation-2 z-50"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <div className="font-semibold text-popover-foreground">
                    {t('header.notifications.title')}
                  </div>
                  <button
                    type="button"
                    onClick={markAllAsSeen}
                    className="text-xs text-primary hover:underline flex items-center"
                    disabled={!userNotifications?.length}
                  >
                    <Icon name="Check" size={14} className="mr-1" />
                    {t('header.notifications.markAll')}
                  </button>
                </div>

                <div className="max-h-[60vh] md:max-h-96 overflow-y-auto divide-y divide-border">
                  {userNotifications?.length ? (
                    userNotifications.map((notification) => {
                      const notificationId = notification?.id ?? notification?._id;
                      const sectionLabel = resolveSectionLabel(notification?.section);
                      const timestamp = formatNotificationTimestamp(
                        notification?.createdAt,
                      );

                      return (
                        <button
                          key={notificationId}
                          type="button"
                          onClick={() => handleNotificationClick(notification)}
                          className={`w-full text-left px-4 py-3 transition-hover ${
                            notification?.seen ? 'bg-transparent' : 'bg-muted/40'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <div className="flex items-start justify-between gap-2">
                                <div className="text-sm font-semibold text-popover-foreground line-clamp-2">
                                  {notification?.title ?? t('header.notifications.untitled')}
                                </div>
                                {!notification?.seen && (
                                  <span className="mt-1 h-2 w-2 rounded-full bg-primary" />
                                )}
                              </div>

                              {notification?.message && (
                                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                                  {notification.message}
                                </p>
                              )}

                              <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                                <span className="px-2 py-0.5 rounded-full bg-muted text-foreground/80">
                                  {sectionLabel}
                                </span>
                                {timestamp && <span>{timestamp}</span>}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                      {t('header.notifications.empty')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* User Menu */}
          <div ref={userMenuRef} className="relative user-menu">
            <Button
              variant="ghost"
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="p-2"
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden ${
                  avatarSrc ? 'bg-muted' : 'bg-primary'
                }`}
              >
                {avatarSrc ? (
                  <Image
                    src={avatarSrc}
                    alt={`${displayName}'s avatar`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Icon name="User" size={16} color="white" />
                )}
              </div>
            </Button>

            {isUserMenuOpen && (
              <div className="absolute top-full right-0 mt-1 w-48 bg-popover border border-border rounded-lg shadow-elevation-2 py-1 z-50">
                <div className="px-4 py-2 border-b border-border">
                  <div className="text-sm font-medium text-popover-foreground">{displayName}</div>
                  <div className="text-xs text-muted-foreground capitalize">{effectiveRole}</div>
                </div>
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-hover"
                >
                  <Icon name="Settings" size={16} className="mr-3" />
                  {t('header.labels.settings')}
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-hover"
                >
                  <Icon name="LogOut" size={16} className="mr-3" />
                  {t('header.labels.signOut')}
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2"
            ref={mobileToggleRef}
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </Button>
        </div>
      </div>
      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div ref={mobileMenuRef} className="lg:hidden bg-card border-t border-border shadow-elevation-2">
          <nav className="px-4 py-2 space-y-1">
            {orderedFilteredNavItems?.map((item) => {
              const isActive = location?.pathname === item?.path;
              return (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-hover relative ${
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={18} className="mr-3" />
                  {item?.label}
                </button>
              );
            })}

            {/* Mobile Location Selector */}
            {(effectiveRole === 'manager' || effectiveRole === 'admin') && showLocationSelector && (
              <div className="pt-2 border-t border-border mt-2">
                <div className="text-xs font-semibold text-muted-foreground px-4 py-2 uppercase tracking-wide">
                  {t('header.labels.location')}
                </div>
                {canChangeLocation ? (
                  <div className="space-y-2 px-3 pb-3">
                    {availableLocations?.map((loc) => {
                      const isActive = isLocationActive(loc);
                      return (
                        <button
                          key={loc?.id ?? `all-${loc?.name}`}
                          onClick={() => {
                            handleLocationSelect(loc);
                            setIsMobileMenuOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 text-sm rounded-xl border transition-all shadow-sm ${
                            isActive
                              ? 'bg-primary/10 border-primary text-foreground'
                              : 'bg-card border-border text-foreground hover:border-muted-foreground/40 hover:-translate-y-0.5'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className={`p-2 rounded-lg bg-muted ${
                                isActive ? 'ring-2 ring-primary/60 ring-offset-1 ring-offset-background' : ''
                              }`}
                            >
                              <Icon name="MapPin" size={16} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold leading-tight truncate">{loc?.name}</div>
                              {loc?.address && (
                                <div className="text-xs text-muted-foreground leading-snug line-clamp-2">{loc.address}</div>
                              )}
                            </div>
                            {isActive && (
                              <div className="w-2.5 h-2.5 rounded-full bg-primary mt-1" aria-hidden="true" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="px-4 py-2 text-sm text-muted-foreground flex items-center">
                    <Icon name="MapPin" size={16} className="mr-3" />
                    {displayedLocationName}
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
